// src/components/dashboard/InvestmentPlans/InvestmentPlans.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '../../../context/CurrencyContext';
import CircularProgress from '../../common/UI/CircularProgress';
import './InvestmentPlans.css';

const InvestmentPlans = ({ plans }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();

  const formatAmount = (investment, field) => {
    const displayField = `${field}_display`;
    
    if (investment[displayField] && investment[displayField].formatted) {
      return investment[displayField].formatted;
    }
    
    return formatCurrency(investment[field] || 0);
  };

  const calculateProgress = (plan) => {
    if (!plan.created_at || !plan.lock_end_date) return 0;

    const startDate = new Date(plan.created_at);
    const endDate = new Date(plan.lock_end_date);
    const currentDate = new Date();

    if (currentDate < startDate) return 0;
    if (currentDate >= endDate) return 100;

    const totalDuration = endDate - startDate;
    const elapsedDuration = currentDate - startDate;
    const progress = (elapsedDuration / totalDuration) * 100;

    return Math.min(Math.max(progress, 0), 100);
  };

  const getDaysRemaining = (lockEndDate) => {
    if (!lockEndDate) return null;
    
    const endDate = new Date(lockEndDate);
    const currentDate = new Date();
    const diffTime = endDate - currentDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Matured';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day';
    return `${diffDays} days`;
  };

  const nextSlide = useCallback(() => {
    if (!plans || plans.length <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % plans.length);
  }, [plans]);

  useEffect(() => {
    if (!plans || plans.length <= 1) return;
    const interval = setInterval(() => nextSlide(), 10000);
    return () => clearInterval(interval);
  }, [nextSlide, plans]);

  const handleNavigateToInvestmentStocks = () => {
    navigate('/investment/stocks');
  };

  const handleNavigateToInvestmentPlans = () => {
    navigate('/investment/plans');
  };

  if (!plans || plans.length === 0) {
    return (
      <div className="investment-plans">
        <h2>Investment Plans</h2>
        <div className="no-plans" onClick={handleNavigateToInvestmentStocks}>
          <div className="no-plans-icon">
            <svg 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="investment-icon"
            >
              <path 
                d="M3 13h2v8H3v-8zm4-7h2v15H7V6zm4 3h2v12h-2V9zm4-3h2v15h-2V6zm4 7h2v8h-2v-8z" 
                fill="currentColor"
              />
              <path 
                d="M2 3h20v2H2V3z" 
                fill="currentColor"
              />
            </svg>
          </div>
          <div className="no-plans-content">
            <h3>Start Your Investment Plan</h3>
            <p>Create a personalized investment plan tailored to your financial goals and risk profile</p>
            <span className="browse-investments-link">Explore Investment Opportunities →</span>
          </div>
        </div>
      </div>
    );
  }

  if (plans.length === 1) {
    const plan = plans[0];
    const progress = calculateProgress(plan);
    const daysRemaining = getDaysRemaining(plan.lock_end_date);

    return (
      <div className="investment-plans" onClick={handleNavigateToInvestmentPlans} style={{cursor: 'pointer'}}>
        <div className="plans-header">
          <h2>Investment Plans</h2>
        </div>
        <div className="single-plan">
          <div className="plan-card">
            <div className="plan-header">
              <div className="plan-title-section">
                <h3 className="plan-name">
                  {plan.questionnaire?.risk_profile || 'Investment Plan'} - {plan.stock?.symbol}
                </h3>
                <div className="plan-duration">{plan.questionnaire?.time_horizon || 'Flexible'}</div>
              </div>
              
              <div className="plan-progress-section">
                <CircularProgress 
                  percentage={progress} 
                  size={70} 
                  strokeWidth={6}
                  color={progress === 100 ? '#15A759' : '#4A90E2'}
                />
                {daysRemaining && <div className="days-remaining">{daysRemaining}</div>}
              </div>
            </div>
            
            <div className="plan-details">
              <div className="plan-detail">
                <span>Allocated:</span>
                <span className="amount">{formatAmount(plan, 'total_invested')}</span>
              </div>
              
              <div className="plan-detail">
                <span>Returns:</span>
                <span className={`returns ${(Number(plan.profit_loss_percentage) || 0) >= 0 ? 'positive' : 'negative'}`}>
                  {((Number(plan.profit_loss_percentage) || 0) >= 0 ? '+' : '')}
                  {(Number(plan.profit_loss_percentage) || 0).toFixed(2)}%
                </span>
              </div>
              
              {plan.lock_end_date && (
                <div className="plan-detail">
                  <span>Matures:</span>
                  <span className="date">{new Date(plan.lock_end_date).toLocaleDateString()}</span>
                </div>
              )}
            </div>
            
            <div className="plan-status">
              <span className={`status ${plan.is_locked ? 'locked' : 'unlocked'}`}>
                {plan.is_locked ? '🔒 Locked' : '🔓 Unlocked'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="investment-plans" onClick={handleNavigateToInvestmentPlans} style={{cursor: 'pointer'}}>
      <div className="plans-header">
        <h2>Investment Plans</h2>
        <div className="carousel-counter">{currentIndex + 1} / {plans.length}</div>
      </div>

      <div className="plans-carousel-horizontal">
        <div className="carousel-track-horizontal">
          <div 
            className="carousel-slides-horizontal"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {plans.map((plan) => {
              const progress = calculateProgress(plan);
              const daysRemaining = getDaysRemaining(plan.lock_end_date);
              
              return (
                <div key={plan.id} className="carousel-slide-horizontal">
                  <div className="plan-card">
                    <div className="plan-header">
                      <div className="plan-title-section">
                        <h3 className="plan-name">
                          {plan.questionnaire?.risk_profile || 'Investment Plan'} - {plan.stock?.symbol}
                        </h3>
                        <div className="plan-duration">{plan.questionnaire?.time_horizon || 'Flexible'}</div>
                      </div>
                      
                      <div className="plan-progress-section">
                        <CircularProgress 
                          percentage={progress} 
                          size={70} 
                          strokeWidth={6}
                          color={progress === 100 ? '#15A759' : '#4A90E2'}
                        />
                        {daysRemaining && <div className="days-remaining">{daysRemaining}</div>}
                      </div>
                    </div>
                    
                    <div className="plan-details">
                      <div className="plan-detail">
                        <span>Allocated:</span>
                        <span className="amount">{formatAmount(plan, 'total_invested')}</span>
                      </div>
                      
                      <div className="plan-detail">
                        <span>Returns:</span>
                        <span className={`returns ${(Number(plan.profit_loss_percentage) || 0) >= 0 ? 'positive' : 'negative'}`}>
                          {((Number(plan.profit_loss_percentage) || 0) >= 0 ? '+' : '')}
                          {(Number(plan.profit_loss_percentage) || 0).toFixed(2)}%
                        </span>
                      </div>
                      
                      {plan.lock_end_date && (
                        <div className="plan-detail">
                          <span>Matures:</span>
                          <span className="date">{new Date(plan.lock_end_date).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="plan-status">
                      <span className={`status ${plan.is_locked ? 'locked' : 'unlocked'}`}>
                        {plan.is_locked ? '🔒 Locked' : '🔓 Unlocked'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestmentPlans;